// Pairwise car-vs-car collision. Cars are treated as circles of
// CAR_BODY_RADIUS — same approximation the wall collision uses, so
// "what touches a wall" and "what touches another car" agree
// visually. Oriented-body / SAT collision is a future polish pass
// when damage modeling is worth the complexity.
//
// Physics: 1-D collision along the contact normal with restitution.
//   - Position correction splits the overlap evenly (each car shifts
//     half the overlap distance away from the contact).
//   - Velocity along the contact normal exchanges per a 1-D collision
//     with restitution e ∈ [0, 1]: 0 = stick, 1 = elastic. We use 0.4
//     — a moderate bounce that feels physical without sending cars
//     pinballing.
//   - Tangential velocity is unchanged (frictionless contact). Real
//     cars would scrub speed via tire side-load on contact; we'll add
//     that with damage modeling.
//   - Angular velocity is damped lightly on contact (off-center hits
//     in real life would induce/transfer spin; modeling the lever arm
//     is overkill here, damping keeps the sim stable).
//
// Equal mass assumption — all cars currently use DEFAULT_CAR_SPEC.
// When per-car mass lands, swap the symmetric formula for the general
// 1-D restitution form.

import { CAR_BODY_RADIUS } from './track-queries.js';
import { isOnPitRoute } from './world.js';
import { applyImpactDamage } from './condition.js';

// Coefficient of restitution. 0 = perfectly inelastic (stick), 1 =
// perfectly elastic (full bounce). 0.4 lets cars "deflect" off each
// other without making contact feel rubbery.
const RESTITUTION = 0.4;

// Multiplier on angular velocity per contact frame. Same range as
// the wall-collision yawDamp (0.92) — a touch heavier here because
// car-car contact is more chaotic than a wall slide.
const ANGULAR_DAMP_ON_CONTACT = 0.85;

// Damage scaling for car-car contact only. The shared
// applyImpactDamage formula is calibrated for wall hits, where a
// 30 m/s frontal stop totals the car. Car-on-car bumps at race pace
// would tally damage too quickly under that same curve, so we
// downscale the impulse passed in. Walls keep their original
// curve; only this multiplier softens car-car incidents.
const CAR_DAMAGE_SCALE = 0.35;

// Two cars are "in contact" when their center distance is below
// 2 × radius. Same radius as wall collision so a car wedged between
// a wall and another car can't squeeze through either contact.
const COLLISION_DIST = 2 * CAR_BODY_RADIUS;

export function applyCarCollisions(cars) {
  const N = cars.length;
  for (let i = 0; i < N; i++) {
    for (let j = i + 1; j < N; j++) {
      // Disable car-car collision while either car is on the pit
      // route. The sim doesn't yet have the choreography we'd need
      // to handle a 25-second pit stop with neighbours pulling in
      // and out around it (queue management, "leave space behind a
      // serviced car", lateral peel-off awareness, etc). Letting
      // pit-route cars pass through each other is the smaller bug
      // until that logic lands; once it does we can re-enable here.
      if (isOnPitRoute(cars[i]) || isOnPitRoute(cars[j])) continue;
      // Recovered cars (towed off after a caution) are no longer on
      // track — skip them entirely.
      if (cars[i]._recovered || cars[j]._recovered) continue;
      resolvePair(cars[i], cars[j]);
    }
  }
}

function resolvePair(a, b) {
  const dx = b.pos.x - a.pos.x;
  const dy = b.pos.y - a.pos.y;
  const dist = Math.hypot(dx, dy);
  if (dist >= COLLISION_DIST) return;

  // Degenerate case: cars exactly stacked (e.g. fresh spawn at the
  // same grid slot due to a config bug). Push along the +x axis to
  // break symmetry — any deterministic axis works.
  if (dist < 1e-6) {
    a.pos.x -= CAR_BODY_RADIUS;
    b.pos.x += CAR_BODY_RADIUS;
    return;
  }

  // Contact normal: from a toward b, unit length.
  const nx = dx / dist;
  const ny = dy / dist;

  // Position correction — split the overlap evenly so neither car
  // jumps the full distance. Stops the "hot potato" oscillation that
  // happens when one side gets all the correction.
  const overlap = COLLISION_DIST - dist;
  const half = overlap * 0.5;
  a.pos.x -= nx * half;
  a.pos.y -= ny * half;
  b.pos.x += nx * half;
  b.pos.y += ny * half;

  // Velocity along the contact normal.
  const vAn = a.vel.x * nx + a.vel.y * ny;
  const vBn = b.vel.x * nx + b.vel.y * ny;
  const approach = vAn - vBn;       // positive = closing
  if (approach <= 0) return;        // already separating; positional pop is enough

  // Equal-mass 1-D restitution: each car's normal velocity changes by
  // ±(1 + e) × approach / 2. Tangential components untouched.
  const dv = (1 + RESTITUTION) * approach * 0.5;
  a.vel.x -= nx * dv;
  a.vel.y -= ny * dv;
  b.vel.x += nx * dv;
  b.vel.y += ny * dv;

  // Both cars take damage proportional to their share of the normal
  // impulse. With equal-mass restitution that share is the same
  // magnitude (mass × dv) on each side — the impact is symmetric, so
  // a 30 m/s closing crash totals both cars equally. Per-car
  // durability (spec.durability) skews that inside applyImpactDamage.
  const impulseA = a.spec.mass * dv * CAR_DAMAGE_SCALE;
  const impulseB = b.spec.mass * dv * CAR_DAMAGE_SCALE;
  applyImpactDamage(a, impulseA);
  applyImpactDamage(b, impulseB);

  a.angVel *= ANGULAR_DAMP_ON_CONTACT;
  b.angVel *= ANGULAR_DAMP_ON_CONTACT;
}
